import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../ManagementPages.module.css';
import Select from 'react-select';
import useAxios from '../../services/AxiosConfig';
import {
    TeamMultiSelectOption as Option,
    TeamMultiSelectMultiValue as MultiValue,
    TeamMultiSelectValueContainer as ValueContainer,
    getTeamMultiSelectStyles,
} from './patientProgramTeamMultiSelect';
import { customStyles } from "../../components/AccountManagement/CustomStyles";
import { setModalitiesState, resetModalitiesState } from './PatientModalitiesSlice';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { setProgramState, setEnrollmentState, setPatientInfo, resetNewProgram, setSelectedProgram, setFormValue, setEnrollmentFormValue } from './PatientEnrollmentSlice';
import { setDirty } from '../../slicers/DirtyCheckSlice';
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import PerinHealthModal from '../../components/PerinHealthModel';
import { openModal, closeModal } from '../../components/PerinHealthModelSlice';
import { TrashIcon } from '../../components/icons/Icons';
import Logger from '../../services/Logger';
import { LogElements, LogAction } from '../../services/LogElements';

const PatientProgramSetup = ({ errors }) => {

    const { patients, programs, devices, questionnaires, content } = useSelector((state) => state.enrollmentLists);
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);

    const [menuIsOpen, setMenuIsOpen] = useState(false); // State to control dropdown visibility
    const dropdownRef = useRef(null); // Ref for the dropdown

    const data = useSelector((state) => state.enrollPatientProgram);

    const { updating, adding, reenrolling, viewing } = data;
    // teams that the signed in user is a part of
    const signedInUserGroups = useSelector((state) => state.userInfo.groups);

    const filteredPrograms = programs.filter((program) => program.value.id === 0 || data.enrollment.groups.some((group) => group.groupId === program.value.groupId));

    // any teams the current patient is a part of that the signed in user is not
    const [excludedUserTeams, setExcludedUserTeams] = useState([]);

    const updatingId = updating ? data.enrollmentId : 0;
    const [currentUpdatingProgram, setCurrentUpdatingProgram] = useState(updatingId);

    const [count, setCount] = useState(0);
    const selectedProgram = useSelector((state) => state.enrollPatientProgram.selectedProgram);

    // all team dropdown options -- all teams patient is in 
    const [teamOptions, setTeamOptions] = useState([]);

    // teams currently assigned to the current enrollment record
    const selectedPatientGroups = data.enrollment.groups;


    const [possibleNewTeams, setPossibleNewTeams] = useState([])

    const patientId = useSelector((state) => state.enrollPatientProgram.patient.id);
    const savedModalityValues = useSelector((state) => state.savedPatientModalities.patientRecentModalities[patientId]) ?? {};
    const savedSerialNumbers = useSelector((state) => state.savedPatientModalities.patientRecentSerialNumbers[patientId]) ?? {};

    const date = new Date();


    const getTodaysDate = () => {
        let convertingString = new Date().toISOString().split('T')[0];
        let returnDate = convertToUTC(convertingString);
        dispatch(setEnrollmentFormValue({key:"startDate", value:returnDate}));
        return returnDate.split('T')[0];
    };

    const convertToUTC = (dateString) => {
        // converting selected date (yyyy-mm-dd) to UTC
        if (!date) return "";

        const timezoneOffset = new Date().getTimezoneOffset() * 60 * 1000;
        const localDate = new Date(dateString);
        const utcDate = new Date(localDate.getTime() + timezoneOffset);
        const utcTimestamp = utcDate.toISOString();
        return utcTimestamp;
    };

    const formatDateFromUTC = (date) => {
        // converting utc time stamp to date input format (yyyy-mm-dd)
        if (date === "") return "";
        if (date.length === 10) return date;
        const utcDate = new Date(date);
        const localDate = utcDate.toLocaleString();
        const splitDate = localDate.split(',')[0].split('/');
        let retDate = new Date(splitDate[2], splitDate[0] - 1, splitDate[1]);
        let formattedDate = retDate.getFullYear() + '-' +
            ('0' + (retDate.getMonth() + 1)).slice(-2) + '-' +
            ('0' + retDate.getDate()).slice(-2);
        return formattedDate;
    };

    let admitDisplayDate = data.enrollment.startDate !== "" ? formatDateFromUTC(data.enrollment.startDate) : getTodaysDate();
    const admitDate = data.enrollment.startDate;
    const dischargeDateDisplay = formatDateFromUTC(data.enrollment.endDate);

    useEffect(() => {
        admitDisplayDate = formatDateFromUTC(data.enrollment.startDate);
    }, [data.enrollment.startDate]);

    useEffect(() => {
        calculateDischargeDate();
    }, [data.enrollment.startDate, data.duration]);

    useEffect(() => {
        // Only call getPatientGroups if patient ID exists
        if (data.patient.id) {
            getPatientGroups(data.patient.id);
        }
    }, []);

    const calculateDischargeDate = () => {
        const duration = data.duration;
        const admitDateSplit = admitDisplayDate.split('-');
        const admitDateObj = new Date(admitDateSplit[0], admitDateSplit[1] - 1, admitDateSplit[2]);
        if (duration === "" || data.enrollment.startDate === "") {
            return "";
        } else {
            admitDateObj.setDate(admitDateObj.getDate() + duration);
            const conversionString = admitDateObj.toISOString().split('T')[0];
            dispatch(setEnrollmentFormValue({key:"endDate", value:convertToUTC(conversionString)}));
        }
    };

    useEffect(() => {
        setSelectedProgram(data.name !== "" ? { name: data.name, id: data.enrollmentId } : { name: "" });
    }, [data]);

    const showPatientError = errors.patient.showing && data.patient.id === "";
    const showProgramError = errors.program.showing && data.name === "";
    const showDurationError = errors.duration.showing && data.duration === "";
    const showGroupsError = errors.group.showing && data.enrollment.groups.length === 0;


    const handleNotes = (e) => {
        dispatch(setDirty());
        setCount(e.target.value.length);
        dispatch(setFormValue({ key: "description", value: e.target.value }));
    };

    const getPatientGroups = (id, initializeSelected = false) => {
        // Add validation to ensure ID is not empty
        if (!id || id === '') {
            console.warn('Patient ID is required to fetch groups');
            return;
        }
        
        httpService.get(`list/groups/${id}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const groupData = responseData.data.data;
                    dispatch(setFormValue({key:"groupId", value:groupData[0].groups[0].groupId}));
                    // Set organizationId for super admin device filtering
                    if (groupData[0] && groupData[0].organizationId) {
                        dispatch(setFormValue({key:"organizationId", value:groupData[0].organizationId}));
                    }

                    // Create a flat array of all teams
                    const allTeams = groupData.flatMap(org =>
                        org.groups.flatMap(group =>
                            group.subgroups.flatMap(subgroup =>
                                subgroup.teams.map(team => ({
                                    organizationId: org.organizationId,
                                    groupId: group.groupId,
                                    subGroupId: subgroup.subGroupId,
                                    teamId: team.teamId,
                                    groupName: group.groupName,
                                    subGroupName: subgroup.subGroupName,
                                    teamName: team.teamName,
                                    value: `${group.groupName} - ${subgroup.subGroupName} - ${team.teamName}`,
                                    label: `${group.groupName} - ${subgroup.subGroupName} - ${team.teamName}`,
                                    key: { "organizationId": org.organizationId, "groupId": group.groupId, "subGroupId": subgroup.subGroupId, "teamId": team.teamId }
                                }))
                            )
                        )
                    );

                    const signedInUserTeams = signedInUserGroups.flatMap(org =>
                        org.groups.flatMap(group =>
                            group.subgroups.flatMap(subgroup =>
                                subgroup.teams.map(team => ({
                                    organizationId: org.organizationId,
                                    groupId: group.groupId,
                                    subGroupId: subgroup.subGroupId,
                                    teamId: team.teamId,
                                    groupName: group.groupName,
                                    subGroupName: subgroup.subGroupName,
                                    teamName: team.teamName
                                }))
                            )
                        )
                    );

                    setTeamOptions(allTeams);
                    setExcludedUserTeams(allTeams.filter(o1 => !signedInUserTeams.some(o2 => o1.teamId === o2.teamId)));


                    if (initializeSelected) {
                        const patientDoctorTeams = allTeams.filter((patientTeam) => signedInUserTeams.find((userTeam) => userTeam.teamId === patientTeam.teamId))
                        dispatch(setEnrollmentFormValue({key:"groups", value:patientDoctorTeams}));
                    }
                } else {
                    console.error("Can't fetch patient groups");
                }
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleChangeInUpdatingProgram = (enrollmentId) => {
        // Log program updating dropdown selection
        const selectedProgram = data.updatingProgramOptions.find(program => program.enrollmentId == enrollmentId);
        const programName = selectedProgram ? selectedProgram.name : 'Unknown Program';
        const patientName = data.patient.firstName && data.patient.lastName ? `${data.patient.firstName} ${data.patient.lastName}` : 'Unknown Patient';
        const customMessage = `${programName} - ${patientName}`;

        Logger.shared.info(
            LogElements.selectProgramDropdown,
            LogAction.clicked,
            'PatientProgramSetup.js',
            customMessage
        );

        dispatch(setEnrollmentFormValue({key:"groups", value:[]}));
        setCurrentUpdatingProgram(enrollmentId);
        const updatingPrograms = data.updatingProgramOptions;
        try {
            httpService.get(`/enrollment/${enrollmentId}`)
                .then((responseData) => {
                    if (responseData.data.data) {
                        const programData = responseData.data.data[0];
                        dispatch(setEnrollmentState(programData));
                        dispatch(setFormValue({ key: "updatingProgramOptions", value: updatingPrograms }));
                        dispatch(setModalitiesState({ programDevices: programData.devices, allDevices: devices, modalityValues: savedModalityValues }));

                        // Fetch and update teams for the new program
                        getPatientGroups(data.patient.id);
                    } else {
                        console.error('Invalid data format:');
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (error) {
            dispatch(
                showAlert({
                    header: "Error",
                    message: `Could not switch programs to update`,
                    type: 'danger'
                })
            );
        }
    }

    const handlePatientSelection = (selected) => {
        // Log patient selection with selected name as custom message
        Logger.shared.info(
            LogElements.patientNameDropdown,
            LogAction.clicked,
            'PatientProgramSetup.js',
            selected.label
        );

        dispatch(setDirty());
        dispatch(setPatientInfo(selected.value));
        dispatch(resetNewProgram());
        dispatch(setEnrollmentFormValue({key:"groups", value:[]}));
        dispatch(setSelectedProgram({ name: "", id: "" }));
        getPatientGroups(selected.value.id, true);
    };


    const handleProgramSelection = (program) => {
        // Log program selection with selected program name as custom message
        Logger.shared.info(
            LogElements.selectProgramDropdown,
            LogAction.clicked,
            'PatientProgramSetup.js',
            program.label
        );

        dispatch(setDirty());
        if (program.label === "Create Custom Program") {
            dispatch(resetNewProgram());
            dispatch(resetModalitiesState());
            dispatch(setSelectedProgram({ name: "Create Custom Program", id: 0 }));
            dispatch(setFormValue({key:"groupId", value:data.enrollment.groups[0].groupId}));
        } else {
            dispatch(setSelectedProgram({ id: program.value.id, name: program.value.name }));
            dispatch(setProgramState({ "program": program.value, "savedSerialNumbers": savedSerialNumbers }));
            dispatch(setModalitiesState({ programDevices: program.value.devices, allDevices: devices, modalityValues: savedModalityValues }));
        }
    };

    const handleChange = (e) => {
        dispatch(setDirty());
        const { id, value } = e.target;
        if (id === "duration") {
            dispatch(setFormValue({ key: id, value: value === "" ? "" : Number(value) }));
            return;
        }
        dispatch(setFormValue({ key: id, value: value }));
    };

    const notesTooltip = (
        <Tooltip id="notes-tooltip">
            Add any additional information, description, or comments about the patient's program here. <strong>This is an optional field.</strong>
        </Tooltip>
    );

    const multiSelectStyles = getTeamMultiSelectStyles(showGroupsError);

    const handleTeamSelection = (selectedOptions) => {
        dispatch(setDirty());
        const newTeams = selectedOptions.filter(o1 => !selectedPatientGroups.some(o2 => o1.teamId === o2.teamId));
        const newTeamsExcluded = newTeams.filter(teamId => excludedUserTeams.includes((teamId)));

        if (newTeamsExcluded.length >= 1) {
            dispatch(
                openModal({
                    activeModalId: "PatientProgramSetup",
                    header: "Add Team to Enrollment?",
                    message: `You are not a part of the ${newTeamsExcluded[0].teamName} team.\n Would you still like to add this team to ${data.patient.firstName + " " + data.patient.lastName}'s enrollment`,
                    buttons: [
                        { label: 'Add', variant: 'primary', onClickKey: "Add" },
                        { label: 'Cancel', variant: 'secondary', onClickKey: "Cancel" },
                    ],
                })
            );
            setPossibleNewTeams(selectedOptions)
        } else {
            dispatch(setEnrollmentFormValue({key:"groups", value:selectedOptions}));
        }
    };

    const handleModalOptions = (onClickKey) => {
        if (onClickKey === "Add") {
            dispatch(closeModal());
            dispatch(setEnrollmentFormValue({key:"groups", value:possibleNewTeams}));
        } else {
            dispatch(closeModal());
        }
    };


    const handleRemoveTeam = (teamToRemove) => {
        const updatedTeams = selectedPatientGroups.filter(team =>
            team.organizationId !== teamToRemove.organizationId ||
            team.groupId !== teamToRemove.groupId ||
            team.subGroupId !== teamToRemove.subGroupId ||
            team.teamId !== teamToRemove.teamId
        );
        const removingGroupId = teamToRemove.groupId;
        const isRemovingLastTeamForGroup = !updatedTeams.some((t) => t.groupId === removingGroupId);

        if (isRemovingLastTeamForGroup) {
            const selectedDevices = data.devices;
            const selectedQuestionnaireIds = data.questionnaires.map((q) => q.questionnaireId);
            const selectedContentIds = data.contents.map((c) => c.contentId);

            const devicesInGroup = selectedDevices.filter((selectedDevice) => {
                // groupId isn't persisted for enrolled devices, so determine membership by checking whether the
                // selected serial number exists within the group-specific device's serialNumbers list.
                const deviceId = selectedDevice?.deviceId;
                const serialNumberId = selectedDevice?.serialNumberId;
                const serialNumber = selectedDevice?.serialNumber;

                if (!deviceId) return false;

                const groupDevice = devices.find((d) => d.deviceId === deviceId && d.groupId === removingGroupId);
                if (!groupDevice) return false;

                const serials = Array.isArray(groupDevice.serialNumbers) ? groupDevice.serialNumbers : [];
                if (serialNumberId) {
                    return serials.some((sn) => sn?.id === serialNumberId);
                }
                if (serialNumber) {
                    return serials.some((sn) => sn?.serialNumber === serialNumber);
                }
                // If no serial is selected yet, fall back to deviceId-only matching for that group
                // (this still correctly blocks removing a group when a device has been added under it).
                return true;
            });
            const questionnairesInGroup = questionnaires.filter((q) => selectedQuestionnaireIds.includes(q.questionnaireId) && q.groupId === removingGroupId);
            const contentInGroup = content.filter((c) => selectedContentIds.includes(c.id) && c.groupId === removingGroupId);

            if (devicesInGroup.length > 0 || questionnairesInGroup.length > 0 || contentInGroup.length > 0) {
                dispatch(
                    showAlert({
                        header: "Error",
                        message: "You can’t remove this group because there are devices, questionnaires, or content assigned under it. Remove those items first.",
                        type: 'danger',
                        dismissable: true
                    })
                );
                return;
            }
        }

        dispatch(setEnrollmentFormValue({key:"groups", value:updatedTeams}));
    };

    const handleMenuToggle = () => {
        setMenuIsOpen((prevState) => !prevState);
    };

    // Function to close dropdown when clicking outside
    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setMenuIsOpen(false); // Close dropdown if clicking outside
        }
    };

    // Add event listener to detect clicks outside the dropdown
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%", paddingBottom: "10px" }}>
            {updating &&
                <div style={{ position: "relative" }}>
                    <span className={"floating-label"} style={{ zIndex: "1" }}>Program Updating</span>
                    <select
                        style={{ width: "300px" }}
                        className={styles["styled-input-select"]}
                        onChange={(e) => handleChangeInUpdatingProgram(e.target.value)}
                        value={currentUpdatingProgram}
                    >
                        {data.updatingProgramOptions.map((enrollment) => (
                            <option key={enrollment.enrollmentId} value={enrollment.enrollmentId}>
                                {enrollment.name}
                            </option>
                        ))}
                    </select>
                </div>
            }
            <div style={{ position: "relative", width: "300px" }}>
                <span className={"floating-label"} style={{ zIndex: "1" }}>Patient Name*</span>
                <Select
                    inputId="enrollment-patient-selector"
                    options={patients}
                    styles={{
                        ...customStyles.popup,
                        container: (provided) => ({
                            ...provided,
                            width: '100%',
                        }),
                        control: (provided, { isDisabled }) => ({
                            ...customStyles.popup.control(provided, { isDisabled }),
                            borderColor: showPatientError ? '#DC3545' : '#686869',
                            '&:hover': {
                                borderColor: showPatientError ? '#DC3545' : '#686869',
                            }
                        }),
                    }}
                    value={{
                        value: { firstName: data.patient.firstName, lastName: data.patient.lastName, id: data.patient.id },
                        label: (data.patient.firstName + ' ' + data.patient.lastName)
                    }}
                    placeholder=''
                    isSearchable={true}
                    isDisabled={adding || updating || reenrolling || viewing}
                    onChange={handlePatientSelection}
                />
            </div>
            {showPatientError &&
                <div className="page-error">
                    {errors.patient.message}
                </div>
            }
            {data.patient.id !== "" &&
                <div style={{position: "relative", width: "100%"}}>
                    <span className={"floating-label"} style={{zIndex:"1"}}>
                        {viewing ? "View Teams" : "Assign Teams"}
                    </span>
                    <div ref={dropdownRef}>
                        <Select
                            key={currentUpdatingProgram}
                            options={teamOptions}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{ Option, MultiValue, ValueContainer }}
                            value={teamOptions.filter(option =>
                                selectedPatientGroups.some(group =>
                                    group.groupId === option.groupId &&
                                    group.subGroupId === option.subGroupId &&
                                    group.teamId === option.teamId
                                )
                            )}
                            onChange={handleTeamSelection}
                            styles={multiSelectStyles}
                            isDisabled={viewing}
                            placeholder=""
                            menuIsOpen={menuIsOpen}
                            onMenuOpen={handleMenuToggle}
                            onMenuClose={() => setMenuIsOpen(false)}
                        />
                    </div>
                </div>
            }
            {selectedPatientGroups.length > 0 && (
                <div>
                    <ul className="groups-list">
                        {selectedPatientGroups.map((group, index) => (
                            <li
                                key={group.teamId}
                                className="group-list-item"
                            >
                                {`${group.groupName} - ${group.subGroupName} - ${group.teamName}`}
                                <div style={{ cursor: "pointer" }} onClick={() => handleRemoveTeam(group)}>
                                    <TrashIcon />
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {showGroupsError && selectedProgram.id !== 0 &&
                <div className="page-error">
                    {errors.group.message}
                </div>
            }
            {data.enrollment.groups.length >= 1 && !updating &&
                <div style={{ position: "relative", width: "300px" }}>
                    <span className={"floating-label"} style={{ zIndex: "1" }}>Select Program*</span>
                    <Select
                        inputId="enrollment-program-selector"
                        options={filteredPrograms}
                        styles={{
                            ...customStyles.popup,
                            container: (provided) => ({
                                ...provided,
                                width: '100%',
                            }),
                            control: (provided, { isDisabled }) => ({
                                ...customStyles.popup.control(provided, { isDisabled }),
                                borderColor: (showProgramError && selectedProgram.id !== 0) ? '#DC3545' : '#686869',
                                '&:hover': {
                                    borderColor: (showProgramError && selectedProgram.id !== 0) ? '#DC3545' : '#686869',
                                }
                            }),
                        }}
                        value={{ value: data.enrollmentId, label: (selectedProgram.name) }}
                        placeholder=''
                        isSearchable={true}
                        isDisabled={reenrolling || viewing}
                        onChange={handleProgramSelection}
                    />
                </div>
            }
            {(showProgramError && selectedProgram.id !== 0) &&
                <div className="page-error">
                    {errors.program.message}
                </div>
            }
            {(selectedProgram.name !== "" || selectedProgram.id === 0) &&
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {selectedProgram.id === 0 &&
                        <div className={styles["styled-input-container"]}>
                            <input
                                id="name"
                                disabled={viewing}
                                defaultValue={data.name}
                                required
                                style={showProgramError && selectedProgram.id === 0 ? { border: "1px solid red", width: "300px" } : { width: "300px" }}
                                onChange={handleChange}
                            ></input>
                            <label className={data.name && styles['filled']}>
                                Program Name*
                            </label>
                        </div>
                    }
                    {showProgramError && selectedProgram.id === 0 &&
                        <div className="page-error">
                            Please choose a program name
                        </div>
                    }
                    <div className={styles["styled-input-container"]} style={{ width: "300px" }}>
                        <div style={{ position: "relative", width: "100%" }}>
                            <label className={data.description && styles['filled']}>
                                Notes
                            </label>
                            <OverlayTrigger placement="right" overlay={notesTooltip}>
                                <FontAwesomeIcon
                                    icon="fa-solid fa-circle-info"
                                    style={{
                                        color: '#686869',
                                        position: 'absolute',
                                        right: '5px',
                                        top: 'calc(50% + 5px)',
                                        transform: 'translateY(10%)'
                                    }}
                                />
                            </OverlayTrigger>
                        </div>
                        <textarea
                            className={styles["note-input"]}
                            id="description"
                            type="text"
                            maxLength={250}
                            value={data.description}
                            style={{ width: "300px" }}
                            disabled={reenrolling || viewing}
                            onChange={e => handleNotes(e)}
                        >
                        </textarea>
                        <span style={{ fontSize: "10px", color: "#929395", marginLeft: "auto" }}>
                            {count}/250
                        </span>
                    </div>
                    <div style={{ display: "flex", columnGap: "5px" }}>
                        <div className={styles["styled-input-container"]}>
                            <input
                                id="admitDate"
                                type="date"
                                value={admitDisplayDate}
                                style={{ width: "145px" }}
                                disabled={updating || viewing}
                                onChange={(e) => dispatch(setEnrollmentFormValue({key:"startDate", value:convertToUTC(e.target.value)}))}
                            ></input>
                            <label className={`${admitDate && styles['filled']} ${updating ? styles['disabled'] : ''}`}>
                                Admit Date
                            </label>
                        </div>
                        <div className={styles["styled-input-container"]}>
                            <span className="floating-label">Expected Discharge Date</span>
                            <input
                                id="dischargeDate"
                                type="date"
                                disabled={true}
                                value={dischargeDateDisplay}
                                style={{ width: "150px" }}
                                onChange={(e) => dispatch(setEnrollmentFormValue({key:"endDate", value:convertToUTC(e.target.value)}))}
                            ></input>
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", columnGap: "10px" }}>
                        <div className={styles["styled-input-container"]}>
                            <input
                                id="duration"
                                inputMode="numeric"
                                onKeyDown={(e) => {
                                    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                                        e.preventDefault();
                                    }
                                }}
                                required
                                value={data.duration}
                                disabled={viewing}
                                style={showDurationError ? { border: "1px solid #DC3545", width: "140px" } : { width: "140px" }}
                                onChange={handleChange}
                            ></input>
                            <label className={data.duration && styles['filled']}>
                                Duration*
                            </label>
                        </div>
                        <span>Days</span>
                    </div>
                    {showDurationError &&
                        <div className="page-error">
                            {errors.duration.message}
                        </div>
                    }
                </div>
            }
            <PerinHealthModal
                modalId={"PatientProgramSetup"}
                handleButtonClick={handleModalOptions}
            />
        </div>
    )
}

export default PatientProgramSetup;